import './index.scss';
import PageHeader from '../PageHeader';
import { EventSingle } from '../../_types/event';
import moment from 'moment-timezone';
import { isEmptyArray } from 'formik';
import { useState, useMemo } from 'react';

type Props = {
    events: EventSingle[] | null;
    isModal?: boolean;
};

const UpComingMatches: React.FC<Props> = ({ events, isModal }) => {
    const [visibleEvents, setVisibleEvents] = useState(4);
    const [selectedFilter, setSelectedFilter] = useState<string>('all');

    // Get unique month-year combinations from events
    const monthYearOptions = useMemo(() => {
        if (!events) return [];
        const uniqueDates = Array.from(new Set(events.map((event) => moment(event.time).format('MMMM YYYY'))));

        const sortableDates = uniqueDates.map((dateStr) => ({
            display: dateStr,
            momentObj: moment(dateStr, 'MMMM YYYY'),
        }));

        sortableDates.sort((a, b) => a.momentObj.valueOf() - b.momentObj.valueOf());
        return ['all', ...sortableDates.map((item) => item.display)];
    }, [events]);

    const filteredEvents = useMemo(() => {
        if (selectedFilter === 'all') {
            return events;
        }

        return events?.filter((event) => moment(event.time).format('MMMM YYYY') === selectedFilter);
    }, [events, selectedFilter]);

    const showMoreEvents = () => {
        setVisibleEvents((prev) => prev + 4);
    };

    const displayedEvents = filteredEvents?.slice(0, visibleEvents);
    const hasMoreEvents = filteredEvents && filteredEvents.length > visibleEvents;

    if (!events) {
        return null;
    }

    return (
        <div style={styles.container}>
            {!isModal && (
                <div className={`text-center mb-6 ${isEmptyArray(events) ? 'hidden' : ''}`}>
                    <PageHeader title="Upcoming Games" position="center" />
                </div>
            )}

            <div style={styles.filterContainer}>
                <select
                    value={selectedFilter}
                    onChange={(e) => {
                        setSelectedFilter(e.target.value);
                        setVisibleEvents(4);
                    }}
                    style={styles.filterSelect}
                >
                    {monthYearOptions.map((option) => (
                        <option key={option} value={option}>
                            {option === 'all' ? 'All Games' : option}
                        </option>
                    ))}
                </select>
            </div>

            <div style={styles.gridContainer} className="card-container">
                {(displayedEvents ?? [])
                    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
                    .map((event, index) => (
                        <div key={index} style={styles.card}>
                            <div style={styles.cardContent}>
                                <div className="image-container">
                                    {event.away_team_logo_url && (
                                        <div className="flex">
                                            <img src={event.away_team_logo_url} alt="Away Logo" className="image" />
                                        </div>
                                    )}
                                </div>
                                <div style={styles.textContainer}>
                                    <h3 style={styles.cardTitle}>{event.title}</h3>
                                    <div style={styles.detailsContainer}>
                                        {moment(event.time).tz(event.timezone).format('dddd, MMM D')} |{' '}
                                        {moment(event.time).tz(event.timezone).format('h:mm A')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>

            {hasMoreEvents && (
                <div className="text-center">
                    <button className="btn btn-outline col-12" onClick={showMoreEvents}>
                        Show More
                    </button>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '1rem',
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '1rem',
        width: '100%',
    },
    filterContainer: {
        marginBottom: '1rem',
        width: '100%',
        maxWidth: '300px',
    },
    filterSelect: {
        padding: '0.5rem',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        width: '100%',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        cursor: 'pointer',
        outline: 'none',
    },
    card: {
        borderRadius: '8px',
        padding: '0.5rem',
        minHeight: '60px',
        width: '100%',
        border: '1px solid #e0e0e0',
    },
    cardContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    imageContainer: {
        flexShrink: 0,
        marginRight: '1rem',
    },
    cardImage: {
        width: '3rem',
        height: '3rem',
    },
    textContainer: {
        flex: 1,
    },
    cardTitle: {
        fontSize: '1rem',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        textDecoration: 'underline',
        textUnderlineOffset: '4px',
    },
    detailsContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '0.1rem',
        fontSize: '0.875rem',
        color: '#666',
    },
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.2rem',
    },
};

export default UpComingMatches;
